import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command'
import { useGlobalSearch, groupSearchResults } from '../hooks/useGlobalSearch'
import { useDebounce } from '@/client/lib/hooks/useDebounce'
import type { SearchEntityType, SearchResultItem } from '@/shared/schemas/search.schema'
import {
  Users,
  Building2,
  Target,
  Briefcase,
  CheckSquare,
  Mail,
  Package,
  FileText,
  BookOpen,
  Search,
  Loader2,
} from 'lucide-react'

// Icons for each entity type
const entityIcons: Record<SearchEntityType, React.ComponentType<{ className?: string }>> = {
  contacts: Users,
  companies: Building2,
  deals: Target,
  cases: Briefcase,
  tasks: CheckSquare,
  enquiries: Mail,
  products: Package,
  notes: FileText,
  docs: BookOpen,
}

// Display names for each entity type
const entityNames: Record<SearchEntityType, string> = {
  contacts: 'Contacts',
  companies: 'Companies',
  deals: 'Deals',
  cases: 'Cases',
  tasks: 'Tasks',
  enquiries: 'Enquiries',
  products: 'Products',
  notes: 'Notes',
  docs: 'Documents',
}

interface GlobalSearchProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)

  const { data, isLoading, isFetching } = useGlobalSearch({
    query: debouncedQuery,
    enabled: open && debouncedQuery.length >= 2,
  })

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  // Handle item selection
  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      onOpenChange(false)
      navigate(item.url)
    },
    [navigate, onOpenChange]
  )

  // Group results by type
  const groupedResults = data?.results ? groupSearchResults(data.results) : null

  // Check if we have any results
  const hasResults = groupedResults && data?.results && data.results.length > 0

  // Render a single result item
  const renderResultItem = (item: SearchResultItem) => {
    const Icon = entityIcons[item.type]
    return (
      <CommandItem
        key={`${item.type}-${item.id}`}
        value={`${item.type}-${item.id}-${item.title}`}
        onSelect={() => handleSelect(item)}
        className="flex items-center gap-3 py-3"
      >
        <Icon className="h-4 w-4 text-muted-foreground" />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="font-medium truncate">{item.title}</span>
          {item.subtitle && (
            <span className="text-xs text-muted-foreground truncate">{item.subtitle}</span>
          )}
        </div>
      </CommandItem>
    )
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Search across contacts, companies, deals, and more"
    >
      <CommandInput
        placeholder="Search contacts, companies, deals..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[400px]">
        {/* Loading state */}
        {(isLoading || isFetching) && query.length >= 2 && (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm">Searching...</span>
          </div>
        )}

        {/* Empty state - no query */}
        {!query && (
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
            <Search className="h-8 w-8 mb-2 opacity-50" />
            <span className="text-sm">Start typing to search...</span>
            <span className="text-xs mt-1">Search across all your data</span>
          </div>
        )}

        {/* Empty state - query too short */}
        {query && query.length < 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Type at least 2 characters to search
          </div>
        )}

        {/* Empty state - no results */}
        {!isLoading && !isFetching && query.length >= 2 && !hasResults && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}

        {/* Results grouped by type */}
        {hasResults &&
          !isLoading &&
          (Object.entries(groupedResults) as [SearchEntityType, SearchResultItem[]][])
            .filter(([, items]) => items.length > 0)
            .map(([type, items], index, arr) => (
              <div key={type}>
                <CommandGroup heading={entityNames[type]}>
                  {items.map(renderResultItem)}
                </CommandGroup>
                {index < arr.length - 1 && <CommandSeparator />}
              </div>
            ))}
      </CommandList>

      {/* Footer with keyboard hints */}
      <div className="border-t px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              ↑↓
            </kbd>{' '}
            Navigate
          </span>
          <span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              ↵
            </kbd>{' '}
            Select
          </span>
          <span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              Esc
            </kbd>{' '}
            Close
          </span>
        </div>
        {data?.totalCount !== undefined && data.totalCount > 0 && (
          <span>{data.totalCount} result{data.totalCount !== 1 ? 's' : ''}</span>
        )}
      </div>
    </CommandDialog>
  )
}
